import { ReviewState, UpdateSource } from '@kainos/shared';
import type { Update } from '@kainos/validation';
import { DEMO_TENANT_ID } from './session.js';
import { dateIn, daysAgo, hoursAgo } from './time.js';

const base = (id: string, activityId: string, projectId: string, at: string, by: string) => ({
  id,
  tenantId: DEMO_TENANT_ID,
  projectId,
  activityId,
  createdAt: at,
  createdBy: by,
  updatedAt: at,
  updatedBy: by,
  reviewState: ReviewState.CONFIRMED,
  evidenceIds: [],
  confirmedBy: by,
  confirmedAt: at,
});

/** Activity history shown in the timeline, newest first per activity. */
export const DEMO_UPDATES: Update[] = [
  {
    ...base('upd_1', 'act_1', 'prj_plantanorte', daysAgo(3), 'usr_daniela'),
    source: UpdateSource.TEXT,
    rawText: 'Iniciamos revisión de planos con el equipo de ingeniería.',
  },
  {
    ...base('upd_2', 'act_2', 'prj_montajeelectrico', daysAgo(2), 'usr_carlos'),
    source: UpdateSource.TEXT,
    rawText:
      'Terminamos la instalación del tablero. Quedaron pendientes dos sensores porque todavía no llegan los materiales. Deberían llegar mañana.',
  },
  {
    ...base('upd_3', 'act_2', 'prj_montajeelectrico', daysAgo(5), 'usr_carlos'),
    source: UpdateSource.TEXT,
    rawText: 'Iniciado montaje de estructura y cableado principal.',
  },
  {
    ...base('upd_4', 'act_3', 'prj_plantanorte', hoursAgo(2), 'usr_felipe'),
    source: UpdateSource.TEXT,
    rawText: 'Avance de 75%, quedan tramos finales de soportería.',
  },
  {
    ...base('upd_5', 'act_4', 'prj_plantanorte', hoursAgo(4), 'usr_camila'),
    source: UpdateSource.TEXT,
    rawText: 'Subida evidencia fotográfica de pruebas en sector A.',
  },
  {
    ...base('upd_6', 'act_5', 'prj_plantanorte', daysAgo(8), 'usr_daniela'),
    source: UpdateSource.SYSTEM,
    rawText: 'Actividad creada, en espera de cierre de Construcción.',
  },
  {
    ...base('upd_7', 'act_6', 'prj_montajeelectrico', daysAgo(2), 'usr_felipe'),
    source: UpdateSource.TEXT,
    rawText: 'No es posible iniciar pruebas hasta que se complete la instalación del tablero.',
  },
  {
    ...base('upd_8', 'act_7', 'prj_bodegacentral', daysAgo(6), 'usr_camila'),
    source: UpdateSource.TEXT,
    rawText: 'Levantamiento finalizado y aprobado.',
  },
  {
    ...base('upd_9', 'act_8', 'prj_flotavehicular', daysAgo(40), 'usr_carlos'),
    source: UpdateSource.TEXT,
    rawText: 'Revisión completada sin observaciones.',
  },
];

/** Feed on the dashboard: cross-project, most recent first. */
export interface FeedEntry {
  id: string;
  authorName: string;
  text: string;
  at: string;
}

export const DEMO_FEED: FeedEntry[] = [
  {
    id: 'feed_1',
    authorName: 'Felipe Soto',
    text: 'Felipe Soto reportó avance en "Montaje piping línea 03" — 75%',
    at: hoursAgo(2),
  },
  {
    id: 'feed_2',
    authorName: 'Camila Araya',
    text: 'Camila Araya subió evidencia fotográfica en "Pruebas de continuidad"',
    at: hoursAgo(4),
  },
  {
    id: 'feed_3',
    authorName: 'KainOS AI',
    text: 'KainOS AI generó el resumen semanal de Ampliación Bodega Central',
    at: daysAgo(1),
  },
  {
    id: 'feed_4',
    authorName: 'Carlos Muñoz',
    text: 'Carlos Muñoz reportó bloqueo en "Instalación tablero eléctrico"',
    at: daysAgo(2),
  },
];

/**
 * Canned interpretation for the "Reportar avance" demo flow.
 * Mirrors what the Update Interpreter returns for the example message.
 */
export const DEMO_INTERPRETATION = {
  activityId: 'act_2',
  progress: 90,
  status: 'IN_PROGRESS',
  blockers: [
    {
      description: 'Faltan dos sensores',
      category: 'MATERIAL',
      estimatedResolutionDate: dateIn(1),
    },
  ],
  summary:
    'Instalación del tablero completada al 90%. Quedan dos sensores pendientes por falta de materiales del proveedor.',
  openQuestions: ['¿Los dos sensores pendientes bloquean la puesta en marcha del tablero?'],
  confidence: 0.93,
} as const;

export const DEMO_EXAMPLE_REPORT =
  'Terminamos la instalación del tablero. Quedaron pendientes dos sensores porque todavía no llegan los materiales. Deberían llegar mañana.';
